import { Button } from "reactstrap";

import {
    ConfigField,
    FieldDef,
    IConfig,
} from "Pages/Setup/Connect/connectShared";
import { INTACCT_FIELDS } from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

/** Fixed for every Billy + Intacct client — not a per-client value. */
const SENDER_ID = "BillyMPP";

/** Authorized client application ids, per Intacct environment. */
const CLIENT_APP_IMPL = "8cb84ce72394e3410bde.app.sage.com";
const CLIENT_APP_PROD = "aa19958a6fa7775be739.app.sage.com";

/** Calance owns the Web Services identity; the client only grants it access.
 * Same pattern as the Billy web login. */
const WS_USER_EMAIL = "intacct-ws@calance.us";

/** Credentials Calance fills in from the emailed Web Services invitation —
 * the client never types these, so they render read-only. */
const CALANCE_MANAGED = ["intacct.api.login_user", "intacct.api.login_pwd"];

const ROLE_PERMISSION_ROWS = [
    ["Accounts Payable", "Vendors", "Yes", "Yes", "Yes", "Yes"],
    ["Accounts Payable", "Vendor types", "Yes", "Yes", "No", "No"],
    ["Purchasing", "Compliance definitions", "Yes", "Yes", "No", "No"],
    ["Purchasing", "Compliance types", "Yes", "Yes", "No", "No"],
    ["Purchasing", "Compliance records", "Yes", "Yes", "Yes", "Yes"],
    ["Purchasing", "Purchasing documents", "Yes", "Yes", "No", "No"],
    ["Projects", "Projects", "Yes", "Yes", "No", "No"],
    ["Company", "Contacts", "Yes", "Yes", "Yes", "Yes"],
    ["Company", "Entities / Locations", "Yes", "Yes", "No", "No"],
    ["Company", "Attachments", "Yes", "Yes", "Yes", "Yes"],
] as const;

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    goTo: (view: string) => void;
}

function CopyRow({ label, value }: { label: string; value: string }) {
    return (
        <div className={styles.copyRow}>
            <b>{label}</b>
            <span className={styles.copyRowValue}>{value}</span>
            <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => void navigator.clipboard?.writeText(value)}
            >
                Copy
            </Button>
        </div>
    );
}

export default function IntacctConnect({ find, onEdit, goTo }: Props) {
    const clientFields = INTACCT_FIELDS.filter(
        (f) => !CALANCE_MANAGED.includes(f.suffix)
    );
    const managedFields = INTACCT_FIELDS.filter((f) =>
        CALANCE_MANAGED.includes(f.suffix)
    );

    const requiredFields = clientFields.filter((f) => f.required);
    const requiredMissing = requiredFields.filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const clientReady = requiredMissing === 0;

    // The Web Services user arrives out-of-band, so "connected" needs both the
    // client-entered values and the Calance-completed credentials.
    const managedReady = managedFields.every((f) => !!find(f.suffix)?.keyValue);
    const connected = clientReady && managedReady;

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoInt}`}>SI</div>
                <div>
                    <div className={styles.pheadName}>Sage Intacct</div>
                    <div className={styles.pheadSub}>
                        Financial ERP · AP, Purchasing, Projects (XML gateway +
                        REST)
                    </div>
                </div>
                {connected ? (
                    <span className={`${styles.pill} ${styles.pillGood}`}>
                        <span className={styles.dot} /> Connected
                    </span>
                ) : clientReady ? (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                        <span className={styles.dot} /> Credentials set — finish
                        authorization
                    </span>
                ) : (
                    <span className={`${styles.pill} ${styles.pillBad}`}>
                        <span className={styles.dot} /> Not connected
                    </span>
                )}
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${styles.tabActive}`}>
                    Connect
                </button>
                <button
                    className={styles.tab}
                    onClick={() => goTo("intaccttest")}
                >
                    Test Connection
                </button>
            </div>

            <div className={styles.wz}>
                <div className={styles.wzLeft}>
                    <h2>Authorize Dimension in Sage Intacct</h2>
                    <p className={styles.lead}>
                        Two things must be authorized in Intacct: our{" "}
                        <strong>Sender ID</strong> and our{" "}
                        <strong>client application</strong>. Then create a Web
                        Services user with a role that can read and write
                        compliance data.
                    </p>

                    <ol className={styles.steps}>
                        <li>
                            Go to{" "}
                            <strong>
                                Company &gt; Setup &gt; Company &gt; Security
                            </strong>
                            . Under <strong>Web Services authorizations</strong>
                            , add the Sender ID shown at right.
                        </li>
                        <li>
                            Right below it, under{" "}
                            <strong>Authorized client applications</strong>, add
                            the client ID for this environment.
                        </li>
                        <li>
                            Add a Web Services user at{" "}
                            <strong>
                                Company &gt; Admin &gt; Web Services users
                            </strong>{" "}
                            using the{" "}
                            <strong>Calance email address at right</strong>.
                            Intacct emails Calance the setup link and we
                            complete the credentials — you do not enter a user id
                            or password below. This user is per-environment, so
                            Production needs its own.
                        </li>
                        <li>
                            Create a role at{" "}
                            <strong>Company &gt; Admin &gt; Roles</strong> (e.g.{" "}
                            <em>Billy Compliance</em>), grant it the permissions
                            at right, then assign it to that user.
                        </li>
                        <li>
                            Enter your <strong>Company ID</strong> and the sender
                            password below.
                        </li>
                    </ol>

                    {clientFields.map((field: FieldDef, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={onEdit}
                        />
                    ))}

                    <div className={styles.calanceBox}>
                        <div className={styles.calanceHead}>
                            Managed by Calance
                        </div>
                        <p className={styles.calanceLead}>
                            Once you send the Web Services user invitation to{" "}
                            <span className={styles.mono}>{WS_USER_EMAIL}</span>
                            , Intacct emails Calance the setup link. We create
                            the credentials and fill these in — nothing for you
                            to do here.
                        </p>

                        {managedFields.map((field) => {
                            const value = find(field.suffix)?.keyValue;
                            return (
                                <div
                                    className={styles.field}
                                    key={field.suffix}
                                    style={{ marginBottom: 0 }}
                                >
                                    <div className={styles.fieldHead}>
                                        <label className={styles.label}>
                                            {field.label}
                                        </label>
                                        <code className={styles.key}>
                                            {field.suffix}
                                        </code>
                                    </div>
                                    {field.help && (
                                        <p className={styles.help}>
                                            {field.help}
                                        </p>
                                    )}
                                    <div className={styles.valueRow}>
                                        <span className={styles.value}>
                                            {value ? (
                                                field.secret ? (
                                                    "••••••••••••"
                                                ) : (
                                                    value
                                                )
                                            ) : (
                                                <span
                                                    className={
                                                        styles.valueEmpty
                                                    }
                                                >
                                                    Awaiting invitation email
                                                </span>
                                            )}
                                        </span>
                                        <span
                                            className={`${styles.flag} ${
                                                value
                                                    ? styles.flagSet
                                                    : styles.flagOptional
                                            }`}
                                            style={{ margin: 0 }}
                                        >
                                            {value
                                                ? "✓ Set by Calance"
                                                : "Pending"}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`${styles.wzRight} ${styles.scrollRight}`}>
                    <p className={styles.rlabel}>Authorize these in Intacct</p>
                    <p className={styles.crumb}>
                        <strong>
                            Company &gt; Setup &gt; Company &gt; Security tab
                        </strong>
                    </p>
                    <CopyRow label="Sender ID" value={SENDER_ID} />
                    <CopyRow label="Client ID (Impl)" value={CLIENT_APP_IMPL} />
                    <CopyRow label="Client ID (Prod)" value={CLIENT_APP_PROD} />
                    <CopyRow label="WS user email" value={WS_USER_EMAIL} />
                    <p className={styles.shotCap} style={{ marginBottom: 16 }}>
                        Sender ID goes in <b>Web Services authorizations</b>; the
                        client ID goes in{" "}
                        <b>Authorized client applications</b> directly below it.
                        Create the Web Services user against the Calance email —
                        Intacct sends the setup link there and we finish it.
                    </p>

                    <p className={styles.rlabel}>Role permissions</p>
                    <p className={styles.crumb}>
                        <strong>Company &gt; Admin &gt; Roles</strong> — create
                        e.g. “Billy Compliance”.
                    </p>
                    <div className={styles.banner}>
                        Draft list — confirm against the permission screenshots
                        in the Intacct foundation setup doc before go-live.
                    </div>
                    <div className={styles.cmicPermTableWrap}>
                        <table className={styles.cmicPermTable}>
                            <thead>
                                <tr>
                                    <th>Module</th>
                                    <th>Area</th>
                                    <th>List</th>
                                    <th>View</th>
                                    <th>Add</th>
                                    <th>Edit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ROLE_PERMISSION_ROWS.map((row) => (
                                    <tr key={`${row[0]}-${row[1]}`}>
                                        <td>{row[0]}</td>
                                        <td>{row[1]}</td>
                                        {[row[2], row[3], row[4], row[5]].map(
                                            (value, i) => (
                                                <td
                                                    key={`${row[0]}-${row[1]}-${i}`}
                                                    className={
                                                        value === "Yes"
                                                            ? styles.cmicPermYes
                                                            : undefined
                                                    }
                                                >
                                                    {value}
                                                </td>
                                            )
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className={styles.shotCap}>
                        The Web Services user is environment-specific — repeat
                        this in Production before go-live.
                    </p>
                </div>
            </div>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo("billy")}>
                    ← Back to Billy
                </Button>
                <Button color="link" onClick={() => goTo("typemap")}>
                    Compliance type mapping next →
                </Button>
                <span className={styles.saveState}>
                    {connected
                        ? "✓ Intacct credentials are set."
                        : !clientReady
                        ? `${requiredMissing} required value${
                              requiredMissing > 1 ? "s" : ""
                          } missing.`
                        : "Waiting on the Web Services invitation to Calance."}
                </span>
            </div>
        </div>
    );
}
